import { useEffect, useMemo } from 'react';
import {
  getCoreRowModel,
  ColumnDef,
  RowSelectionState,
  Row,
} from '@tanstack/react-table';
import { OnChangeFn } from '@tanstack/table-core';
import { Empty } from 'antd';
import { AdaptiveTable, AdaptiveTableProps } from '@/shared/ui';
import {
  FileExplorerContextProps,
  FileExplorerContextProvider,
} from './FileExplorerContext';
import { getStaticColumns } from './helpers';
import { ColumnType } from './types';

type FileExplorerTableProps<TData = unknown> = Omit<
  AdaptiveTableProps<TData>,
  'options'
> &
  Partial<FileExplorerContextProps<TData>> & {
    data?: TData[];
    columns?: ColumnDef<TData>[];
    emptyText?: React.ReactNode;
    enableRowSelection?: ((row: Row<TData>) => boolean) | boolean;
    showRowsSelection?: boolean;
    autoResetRowsSelection?: boolean;
    rowSelection?: RowSelectionState;
    onRowSelect?: OnChangeFn<RowSelectionState>;
  };

const defaultData: FileExplorerTableProps['data'] = [];

export function FileExplorerTable<TData>({
  columns: inputColumns = [],
  data = defaultData as TData[],
  emptyText = 'No data',
  enableRowSelection,
  showRowsSelection,
  autoResetRowsSelection = true,
  rowSelection,
  onRowSelect,
  onRowClick,
  getRowId,
  getRowName,
  getRowTypeIcon,
  isRowClickable,
  rowIdKey = 'id' as keyof TData,
  rowNameKey = 'name' as keyof TData,
  rowTypeKey = 'fileType' as keyof TData,
  ...rest
}: FileExplorerTableProps<TData>) {
  const emptyPlaceholder = useMemo(
    () => (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />
    ),
    [emptyText],
  );

  const config = useMemo((): AdaptiveTableProps<TData>['options'] => {
    const rawColumns = getStaticColumns<TData>();
    const columns = [...inputColumns];
    columns.unshift(rawColumns[ColumnType.Name]);
    if (showRowsSelection) {
      columns.unshift(rawColumns[ColumnType.Select]);
    }
    return {
      data,
      columns,
      enableRowSelection,
      state: { rowSelection },
      onRowSelectionChange: onRowSelect,
      getRowId: (row) => String(row[rowIdKey]),
      getCoreRowModel: getCoreRowModel(),
    };
  }, [data, inputColumns, enableRowSelection, rowSelection, onRowSelect]);

  useEffect(() => {
    if (autoResetRowsSelection) {
      onRowSelect?.({});
    }
  }, [data, autoResetRowsSelection, onRowSelect]);

  return (
    <FileExplorerContextProvider<TData>
      isRowClickable={isRowClickable}
      rowIdKey={rowIdKey}
      rowNameKey={rowNameKey}
      rowTypeKey={rowTypeKey}
      getRowId={getRowId}
      getRowName={getRowName}
      getRowTypeIcon={getRowTypeIcon}
      onRowClick={onRowClick}
    >
      <AdaptiveTable
        placeholder={emptyPlaceholder}
        options={config}
        breakpoint={1200}
        {...rest}
      />
    </FileExplorerContextProvider>
  );
}
